import DataTable from "@/components/data-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGetAuthenticationData } from "@/hooks/use-postmaster";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { type ColDef } from "ag-grid-community";
import { format } from "date-fns";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useMemo } from "react";
import {
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

function AuthTabContent({ date, domain }: { date: string; domain: string }) {
  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    const start = new Date();

    switch (date) {
      case "last7days":
        start.setDate(end.getDate() - 7);
        break;
      case "last30days":
        start.setDate(end.getDate() - 30);
        break;
      case "last60days":
        start.setDate(end.getDate() - 60);
        break;
      case "last90days":
        start.setDate(end.getDate() - 90);
        break;
      case "last120days":
        start.setDate(end.getDate() - 120);
        break;
      default:
        start.setDate(end.getDate() - 7);
    }

    return { startDate: start, endDate: end };
  }, [date]);

  const { data, isLoading } = useGetAuthenticationData({
    domain,
    startDate,
    endDate,
  });

  const columnDefs: ColDef[] = [
    {
      field: "date",
      headerName: "Date",
      filter: true,
      sortable: true,
      valueFormatter: (params) =>
        format(new Date(params.value), "MMM dd, yyyy"),
    },
    {
      field: "dkim",
      headerName: "DKIM success rate",
      filter: true,
      sortable: true,
      valueFormatter: (params) =>
        `${(params.value == null || params.value === 0 ? 100 : params.value).toFixed(1)}%`,
    },
    {
      field: "spf",
      headerName: "SPF success rate",
      filter: true,
      sortable: true,
      valueFormatter: (params) =>
        `${(params.value == null || params.value === 0 ? 100 : params.value).toFixed(1)}%`,
    },
    {
      field: "dmarc",
      headerName: "DMARC success rate",
      filter: true,
      sortable: true,
      valueFormatter: (params) =>
        `${(params.value == null || params.value === 0 ? 100 : params.value).toFixed(1)}%`,
    },
  ];

  const formatAuthRate = (value: number | null | undefined) =>
    value == null || value === 0 ? 100 : value;

  const { avgDkim, avgSpf, avgDmarc } = useMemo(() => {
    const chartData = data?.chartData ?? [];
    if (chartData.length === 0)
      return { avgDkim: 0, avgSpf: 0, avgDmarc: 0 };
    const sumDkim = chartData.reduce((s, i) => s + formatAuthRate(i.dkim), 0);
    const sumSpf = chartData.reduce((s, i) => s + formatAuthRate(i.spf), 0);
    const sumDmarc = chartData.reduce((s, i) => s + formatAuthRate(i.dmarc), 0);
    const n = chartData.length;
    return {
      avgDkim: sumDkim / n,
      avgSpf: sumSpf / n,
      avgDmarc: sumDmarc / n,
    };
  }, [data?.chartData]);

  const formattedChartData = data?.chartData.map((item) => ({
    ...item,
    date: format(new Date(item.date), "MMM dd, yyyy"),
    dkim: formatAuthRate(item.dkim),
    spf: formatAuthRate(item.spf),
    dmarc: formatAuthRate(item.dmarc),
  }));

  if (isLoading)
    return (
      <div className="flex justify-center items-center h-[50vh] w-full text-foreground">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        <Card className="shadow-none">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">
              DKIM Authentication
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">
                  {avgDkim.toFixed(1)}%
                </div>
                <p className="text-xs text-muted-foreground">Success rate</p>
              </div>
              <CheckCircle2
                className={`h-5 w-5 ${
                  data?.statistics.dkim.status === "Good"
                    ? "text-blue-500"
                    : "text-yellow-500"
                }`}
              />
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-none">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">
              SPF Authentication
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">
                  {avgSpf.toFixed(1)}%
                </div>
                <p className="text-xs text-muted-foreground">Success rate</p>
              </div>
              <CheckCircle2
                className={`h-5 w-5 ${
                  data?.statistics.spf.status === "Good"
                    ? "text-green-500"
                    : "text-red-500"
                }`}
              />
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-none">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">
              DMARC Authentication
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">
                  {avgDmarc.toFixed(1)}%
                </div>
                <p className="text-xs text-muted-foreground">Success rate</p>
              </div>
              <CheckCircle2
                className={`h-5 w-5 ${
                  data?.statistics.dmarc.status === "Good"
                    ? "text-green-500"
                    : "text-red-500"
                }`}
              />
            </div>
          </CardContent>
        </Card>
      </div>
      <Card className="shadow-none">
        <CardHeader className="p-4">
          <CardTitle>Authentication Traffic</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={formattedChartData}
                margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
              >
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12 }}
                  tickMargin={10}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  ticks={[0, 20, 40, 60, 80, 100]}
                  domain={[0, 100]}
                  tickFormatter={(value) => `${value}%`}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--background))",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                  }}
                  formatter={(value: number) => [
                    `${(value == null || value === 0 ? 100 : value).toFixed(1)}%`,
                    "Success Rate",
                  ]}
                  labelFormatter={(label) =>
                    format(new Date(label), "MMM dd, yyyy")
                  }
                />
                <Line
                  type="monotone"
                  dataKey="dkim"
                  name="DKIM success rate"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  dot={{ fill: "#3B82F6", strokeWidth: 2, r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="spf"
                  name="SPF success rate"
                  stroke="#EF4444"
                  strokeWidth={2}
                  dot={{ fill: "#EF4444", strokeWidth: 2, r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="dmarc"
                  name="DMARC success rate"
                  stroke="#F59E0B"
                  strokeWidth={2}
                  dot={{ fill: "#F59E0B", strokeWidth: 2, r: 4 }}
                />
                <Legend />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
      <div className="mt-6">
        <DataTable
          rowData={data?.chartData || []}
          columnDefs={columnDefs}
          height={400}
        />
      </div>
    </div>
  );
}

// Export both the component for use in other files and the Route for the router
export { AuthTabContent };

function AuthTabRouteComponent() {
  // Get domain and date from search params if available, otherwise use defaults
  const search = useSearch({ strict: false });
  const domain = (search as { domain?: string })?.domain || "";
  const date = (search as { date?: string })?.date || "last7days";
  return <AuthTabContent date={date} domain={domain} />;
}

export const Route = createFileRoute("/_private/postmaster/tabs/auth-tab")({
  component: AuthTabRouteComponent,
});
